import { useState, useEffect } from 'react';

const PickYourOwn = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('pick-your-own');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const experiences = [
    {
      title: 'Strawberry Zone 🍓',
      description: 'Pick fresh, juicy strawberries directly from our organic patches',
      image: 'https://readdy.ai/api/search-image?query=Indian%20family%20picking%20fresh%20strawberries%20in%20organic%20farm%2C%20hands%20gently%20harvesting%20ripe%20red%20strawberries%2C%20children%20and%20adults%20enjoying%20strawberry%20picking%20experience%2C%20lush%20green%20strawberry%20plants%2C%20joyful%20farming%20activity%2C%20natural%20sunlight&width=500&height=400&seq=strawberry-picking&orientation=landscape',
      icon: 'ri-heart-fill',
      color: 'text-red-500'
    },
    {
      title: 'Square Inch Gardening',
      description: 'Learn intensive gardening techniques in small spaces',
      image: 'https://readdy.ai/api/search-image?query=Square%20foot%20gardening%20demonstration%20in%20Indian%20farm%2C%20organized%20vegetable%20plots%20in%20small%20squares%2C%20efficient%20space%20utilization%2C%20Indian%20farmers%20teaching%20gardening%20techniques%2C%20neat%20organized%20garden%20beds%2C%20sustainable%20farming%20methods&width=500&height=400&seq=square-gardening&orientation=landscape',
      icon: 'ri-grid-line',
      color: 'text-green-500'
    },
    {
      title: 'Plant-Based Farming',
      description: 'Discover organic, chemical-free growing methods',
      image: 'https://readdy.ai/api/search-image?query=Organic%20plant-based%20farming%20in%20India%2C%20farmers%20using%20natural%20fertilizers%20and%20compost%2C%20healthy%20vegetable%20plants%20growing%20without%20chemicals%2C%20sustainable%20agriculture%20practices%2C%20green%20farming%20techniques%2C%20eco-friendly%20cultivation&width=500&height=400&seq=plant-based-farming&orientation=landscape',
      icon: 'ri-leaf-line',
      color: 'text-green-600'
    },
    {
      title: 'Soil Ecology & No-Dig',
      description: 'Understand soil health and no-till farming practices',
      image: 'https://readdy.ai/api/search-image?query=Soil%20ecology%20demonstration%20in%20Indian%20farm%2C%20rich%20dark%20compost%20and%20healthy%20soil%2C%20no-dig%20farming%20methods%2C%20earthworms%20and%20soil%20organisms%2C%20natural%20soil%20health%2C%20sustainable%20agriculture%20education%2C%20organic%20matter%20decomposition&width=500&height=400&seq=soil-ecology&orientation=landscape',
      icon: 'ri-seedling-line',
      color: 'text-brown-500'
    },
    {
      title: 'Compost Display',
      description: 'See how organic waste transforms into rich fertilizer',
      image: 'https://readdy.ai/api/search-image?query=Compost%20making%20demonstration%20in%20Indian%20farm%2C%20organic%20waste%20decomposition%20process%2C%20farmers%20showing%20compost%20bins%20and%20techniques%2C%20rich%20dark%20compost%20soil%2C%20sustainable%20waste%20management%2C%20natural%20fertilizer%20production&width=500&height=400&seq=compost-display&orientation=landscape',
      icon: 'ri-recycle-line',
      color: 'text-amber-600'
    },
    {
      title: 'Fresh from Garden',
      description: 'Harvest vegetables and herbs at peak freshness',
      image: 'https://readdy.ai/api/search-image?query=Indian%20family%20harvesting%20fresh%20vegetables%20and%20herbs%20from%20garden%2C%20hands%20picking%20tomatoes%20lettuce%20and%20herbs%2C%20farm-to-table%20experience%2C%20fresh%20organic%20produce%2C%20joyful%20harvesting%20activity%2C%20colorful%20vegetables%20and%20greens&width=500&height=400&seq=fresh-harvest&orientation=landscape',
      icon: 'ri-plant-line',
      color: 'text-emerald-500'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-green-50 to-emerald-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-16 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            Pick Your Own Experience
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Get your hands dirty and connect with nature through interactive farming experiences
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {experiences.map((experience, index) => (
            <div 
              key={index}
              className={`group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 cursor-pointer ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}
              style={{ transitionDelay: `${200 + index * 100}ms` }}
              onMouseEnter={() => setHoveredCard(index)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={experience.image}
                  alt={experience.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                
                {/* Animated picking hands effect */}
                {hoveredCard === index && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="animate-bounce">
                      <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                        <i className="ri-hand-heart-line text-2xl text-white"></i>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="p-6">
                <div className="flex items-center mb-3">
                  <div className={`w-8 h-8 flex items-center justify-center mr-3 ${experience.color}`}>
                    <i className={`${experience.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">{experience.title}</h3>
                </div>
                <p className="text-gray-600 leading-relaxed">{experience.description}</p>
                
                <div className="mt-4 flex items-center text-green-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-5 h-5 flex items-center justify-center mr-2">
                    <i className="ri-hand-heart-line text-sm"></i>
                  </div>
                  <span className="text-sm font-medium">Interactive Experience</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className={`mt-16 text-center transform transition-all duration-1000 delay-800 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Ready to Get Your Hands Dirty?</h3>
            <p className="text-gray-600 mb-6">Join us for an unforgettable farm experience where you can pick, learn, and connect with nature.</p>
            <button className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer">
              Book Your Experience
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PickYourOwn;